
import React from "react";
import { QuestionDisplay } from "./QuestionDisplay";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { Question } from "@/types/database";

interface GroupQuestionsDisplayProps {
  questions: Question[];
  responses: Record<string, { answer?: string; selected_option?: string }>;
  onAnswerChange: (questionId: string, answer: string) => void;
  onOptionSelect: (questionId: string, option: string) => void;
  groupName?: string;
}

export const GroupQuestionsDisplay = React.memo(
  ({
    questions,
    responses,
    onAnswerChange,
    onOptionSelect,
    groupName,
  }: GroupQuestionsDisplayProps) => {
    if (!questions || questions.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500">No questions available in this group.</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {groupName || "Assessment Questions"}
          </h2>
          <p className="text-gray-600">
            Please answer all questions in this section. You can navigate between sections using the buttons below.
          </p>
        </div>

        {questions.map((question, index) => (
          <Card key={question.id} className="animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center justify-between">
                <span className="text-lg">Question {index + 1}</span>
                <span className="text-xs bg-gray-100 px-2 py-1 rounded uppercase tracking-wide">
                  {question.question_type === "mcq" ? "Multiple Choice" : "Open Ended"}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <QuestionDisplay
                question={question}
                response={responses[question.id] || {}}
                onAnswerChange={(answer) => onAnswerChange(question.id, answer)}
                onOptionSelect={(option) => onOptionSelect(question.id, option)}
              />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
);

GroupQuestionsDisplay.displayName = "GroupQuestionsDisplay";
